/**
 * The floating-combat-text layer's **pure** state machine (Player duels, PRD #252,
 * ADR-0131): the red damage numbers that rise over a struck duelist (a miss floats
 * nothing - only its whiff sounds). It is the net-new client system this
 * slice adds, and it is kept free of Pixi and the DOM so it is node-testable in
 * isolation - exactly the way {@link SoundObserver} keeps the *decision* (which
 * sound, how loud) separate from {@link AudioManager}'s *playback*.
 *
 * A {@link CombatTextLayer} owns a small set of live floats, each aged by the
 * render clock (the same `dtMs` the renderer feeds its sprites). It answers two
 * questions the renderer needs each frame - "which floats are still alive, and for
 * each, how far has it risen and how far has it faded?" - and nothing else; the
 * renderer turns each {@link ActiveCombatFloat} into a `BitmapText` positioned over
 * the target sprite. Floats are keyed by a monotonic `id` so the renderer can pair
 * each with its own label across frames and destroy a label the moment its float
 * expires. No wall clock, no randomness, no Pixi: pure math over its own list.
 *
 * **Damage-number punch (Arena hit feedback, PRD #430 Slice B, ADR-0223):**
 * {@link spawn} optionally takes `{ magnitude, seed }` and the float grows three
 * derived-from-age siblings of offsetY/alpha - {@link combatFloatScale} (a
 * log-curve magnitude scale under an ease-out-back pop), {@link combatFloatOffsetX}
 * (a deterministic seeded scatter, the `mulberry32` precedent from
 * `xpOrbSprite.ts` - no `Math.random` here), and a white-hot color grade for the
 * top band ({@link combatFloatColor}). A spawn WITHOUT the punch options renders
 * pixel-identical to before (scale 1, offsetX 0, the linear rise): the town/duel
 * path is untouched by construction.
 */

import { ARENA_SKILL_DAMAGE } from '../sim/arenaWeapons';

/** How long a combat float lives before it has fully risen and faded (ms). */
export const COMBAT_TEXT_TTL_MS = 900;

/** The trailing slice of the life over which a float fades from full to nothing (ms). */
export const COMBAT_TEXT_FADE_MS = 350;

/** Total upward drift over a float's life, in local (pre-camera) pixels. */
export const COMBAT_TEXT_RISE_PX = 30;

/**
 * Ceiling on how many floats stack over one target at once; a flurry of blows
 * drops the oldest rather than piling up without bound (the overhead-speech
 * precedent). Small, since a duel lands at most one number every couple seconds.
 */
export const COMBAT_TEXT_MAX = 6;

/**
 * The magnitude-scale window over the pop: a punch float's scale eases out-back
 * from 0, overshoots its settle by ~15%, and settles exactly at the window's end.
 */
export const COMBAT_TEXT_POP_MS = 120;

/**
 * The ease-out-back "back" coefficient: tuned so the pop's overshoot peaks at
 * ~1.15x the settle scale (the standard 1.70158 peaks at only ~1.10x).
 */
export const COMBAT_TEXT_POP_BACK = 2.2;

/**
 * How much bigger a number gets per doubling of damage over the base arrow's
 * {@link ARENA_SKILL_DAMAGE}: `1.0 + k * log2(damage / base)`. Anchoring on the
 * base skill damage (not a literal 20) means the curve survives a damage retune.
 */
export const COMBAT_TEXT_SCALE_PER_DOUBLING = 0.25;

/** The magnitude scale's ceiling: a cataclysm crunch caps out, never fills the screen. */
export const COMBAT_TEXT_SCALE_MAX = 1.8;

/** Half-width of the seeded horizontal scatter fan, in local pixels. */
export const COMBAT_TEXT_SCATTER_PX = 16;

/** The magnitude scale at which the color starts grading toward white-hot. */
export const COMBAT_TEXT_WHITE_HOT_START = 1.5;

/** The maximum toward-white blend fraction, reached at the scale clamp. */
export const COMBAT_TEXT_WHITE_HOT_MAX = 0.65;

/**
 * How far a float has risen (a negative y-offset) at `ageMs` into its life.
 * The default (town/duel) rise is the original linear drift - pixel-identical
 * to before the punch fields; a punch float leads with an ease-out rise that
 * still lands on the same full rise at end of life.
 */
export function combatFloatOffsetY(ageMs: number, eased = false): number {
  const t = Math.max(0, Math.min(1, ageMs / COMBAT_TEXT_TTL_MS));
  const p = eased ? 1 - (1 - t) ** 3 : t;
  return -COMBAT_TEXT_RISE_PX * p + 0; // `+ 0` normalises -0 to 0 at t === 0
}

/**
 * The magnitude tier a damage number settles at: `1.0 + k * log2(damage / base)`
 * clamped to `[1.0, COMBAT_TEXT_SCALE_MAX]`. There is no crit mechanic in the sim
 * (PRD #430) - magnitude IS the tiering.
 */
export function combatFloatMagnitudeScale(damage: number): number {
  if (!(damage > 0)) return 1.0;
  const raw = 1.0 + COMBAT_TEXT_SCALE_PER_DOUBLING * Math.log2(damage / ARENA_SKILL_DAMAGE);
  return Math.max(1.0, Math.min(COMBAT_TEXT_SCALE_MAX, raw));
}

/** Ease-out-back over `[0, 1]`: from 0, past 1 (~1.15 peak), settling at exactly 1. */
function easeOutBack(t: number): number {
  const c1 = COMBAT_TEXT_POP_BACK;
  const c3 = c1 + 1;
  const s = t - 1;
  return 1 + c3 * s * s * s + c1 * s * s;
}

/**
 * A float's scale at `ageMs`: the magnitude tier under the pop easing. Without a
 * magnitude (a town/duel float) it is the constant 1.0 - no pop, exactly as today.
 */
export function combatFloatScale(ageMs: number, magnitude?: number): number {
  if (magnitude === undefined) return 1.0;
  const settle = combatFloatMagnitudeScale(magnitude);
  if (ageMs >= COMBAT_TEXT_POP_MS) return settle;
  const t = Math.max(0, ageMs / COMBAT_TEXT_POP_MS);
  return settle * easeOutBack(t);
}

/** Tiny deterministic PRNG (mulberry32), the `xpOrbSprite.ts` precedent. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A float's fixed horizontal scatter: a deterministic seeded offset in
 * `[-COMBAT_TEXT_SCATTER_PX, +COMBAT_TEXT_SCATTER_PX]` so a volley's floats fan
 * out readably instead of stacking. No seed (a town/duel float) = exactly 0.
 */
export function combatFloatOffsetX(seed?: number): number {
  if (seed === undefined) return 0;
  const r = mulberry32(seed || 1)();
  return (r * 2 - 1) * COMBAT_TEXT_SCATTER_PX;
}

/**
 * Mix a `(target, salt)` pair into one scatter seed - the caller's deterministic
 * seed derivation (the arena volley path salts with a per-push float index so two
 * hits on one target in the same volley still fan apart).
 */
export function combatFloatSeed(target: number, salt: number): number {
  return (Math.imul(target + 1, 0x9e3779b1) ^ Math.imul(salt + 1, 0x85ebca6b)) >>> 0;
}

/**
 * The float's color: `base` (the caller's dealt color) through the low and mid
 * magnitude bands, grading toward white-hot across the top band (from
 * {@link COMBAT_TEXT_WHITE_HOT_START} up to the scale clamp). No magnitude = base.
 */
export function combatFloatColor(base: number, magnitude?: number): number {
  if (magnitude === undefined) return base;
  const scale = combatFloatMagnitudeScale(magnitude);
  if (scale <= COMBAT_TEXT_WHITE_HOT_START) return base;
  const band =
    (scale - COMBAT_TEXT_WHITE_HOT_START) / (COMBAT_TEXT_SCALE_MAX - COMBAT_TEXT_WHITE_HOT_START);
  const f = Math.min(1, band) * COMBAT_TEXT_WHITE_HOT_MAX;
  const r = (base >> 16) & 0xff;
  const g = (base >> 8) & 0xff;
  const b = base & 0xff;
  const lerp = (c: number) => Math.round(c + (255 - c) * f);
  return (lerp(r) << 16) | (lerp(g) << 8) | lerp(b);
}

/** A float's alpha at `ageMs`: fully opaque until the fade window, then ramping to 0. */
export function combatFloatAlpha(ageMs: number): number {
  const remaining = COMBAT_TEXT_TTL_MS - ageMs;
  if (remaining >= COMBAT_TEXT_FADE_MS) return 1;
  return Math.max(0, Math.min(1, remaining / COMBAT_TEXT_FADE_MS));
}

/** Whether a float has outlived its TTL and should be dropped. */
export function combatFloatExpired(ageMs: number): boolean {
  return ageMs >= COMBAT_TEXT_TTL_MS;
}

/**
 * The optional damage-number punch (ADR-0223): `magnitude` is the raw damage
 * (drives scale + white-hot grade), `seed` the deterministic scatter. Existing
 * callers pass neither and render exactly as before.
 */
export interface CombatFloatPunch {
  magnitude: number;
  seed: number;
}

/** One live float: what to draw and its raw age (the renderer derives offset/alpha). */
interface CombatFloat {
  id: number;
  text: string;
  /** Flat sRGB colour the renderer tints the label (the combat-red damage number). */
  color: number;
  ageMs: number;
  /** The punch options, absent on the default (town/duel) path. */
  punch?: CombatFloatPunch;
}

/** A float as the renderer consumes it each frame: its id, look, and derived motion. */
export interface ActiveCombatFloat {
  id: number;
  text: string;
  color: number;
  /** Fixed horizontal scatter in local pixels (0 on the default path). */
  offsetX: number;
  /** Upward offset in local pixels (negative = higher). */
  offsetY: number;
  /** `[0, 1]` opacity. */
  alpha: number;
  /** Label scale: the pop + magnitude tier (the constant 1 on the default path). */
  scale: number;
}

/**
 * A target's live combat floats. One per struck/whiffing sprite; the renderer
 * spawns into it on a `CombatText` message, advances it each frame, and reconciles
 * its `BitmapText` labels to {@link active}. Pure and clock-free.
 */
export class CombatTextLayer {
  private floats: CombatFloat[] = [];
  private nextId = 1;

  /** Whether any floats are currently live (the renderer skips idle sprites). */
  get size(): number {
    return this.floats.length;
  }

  /**
   * Spawn a fresh float and return its `id`. Over the {@link COMBAT_TEXT_MAX} cap
   * the oldest is evicted (and its id returned in the next {@link advance}'s
   * removed list is *not* how it leaves - eviction here just drops it, so the
   * caller should treat an id it never sees again in {@link active} as gone). To
   * keep the caller's label bookkeeping simple, eviction ids are reported too.
   */
  spawn(text: string, color: number, punch?: CombatFloatPunch): { id: number; evicted: number[] } {
    const id = this.nextId++;
    // The white-hot grade is fixed at spawn (color is per-float constant, not
    // age-derived); without a punch, combatFloatColor returns `color` untouched.
    this.floats.push({ id, text, color: combatFloatColor(color, punch?.magnitude), ageMs: 0, punch });
    const evicted: number[] = [];
    while (this.floats.length > COMBAT_TEXT_MAX) evicted.push(this.floats.shift()!.id);
    return { id, evicted };
  }

  /**
   * Age every float by `dtMs` and drop those past their TTL. Returns the ids that
   * left this tick so the renderer can destroy their labels. Pure over the list.
   */
  advance(dtMs: number): number[] {
    const removed: number[] = [];
    this.floats = this.floats.filter((f) => {
      f.ageMs += dtMs;
      if (combatFloatExpired(f.ageMs)) {
        removed.push(f.id);
        return false;
      }
      return true;
    });
    return removed;
  }

  /**
   * Drop every live float without reporting removals. Used when the sprite
   * carrying them is recycled for a different character (PRD #826): the caller
   * already owns the labels it is retiring, so there is nothing to report back.
   */
  clear(): void {
    this.floats = [];
  }

  /** The live floats with their derived offset/alpha/scale, oldest first. */
  active(): ActiveCombatFloat[] {
    return this.floats.map((f) => ({
      id: f.id,
      text: f.text,
      color: f.color,
      offsetX: combatFloatOffsetX(f.punch?.seed),
      offsetY: combatFloatOffsetY(f.ageMs, f.punch !== undefined),
      alpha: combatFloatAlpha(f.ageMs),
      scale: combatFloatScale(f.ageMs, f.punch?.magnitude),
    }));
  }
}
